const getVariationsWithPrefixes = (baseNumber: string, withPlus: boolean = false) => {
  const variations = new Set<string>();
  variations.add(baseNumber);
  variations.add(`55${baseNumber}`);
  if (withPlus) {
    variations.add(`+55${baseNumber}`);
  }
  return Array.from(variations);
};

export const getSingle9BrazilVariations = (phone: string, withPlus: boolean = false) => {
  const variations = new Set<string>();
  const numberWithoutCountry = phone.substring(2);
  const doubleNinePos = numberWithoutCountry.indexOf("99");
  if (doubleNinePos === 1) {
    const oldFormat = `${numberWithoutCountry.substring(0, 1)}9${numberWithoutCountry.substring(3)}`;
    getVariationsWithPrefixes(oldFormat, withPlus).forEach((variation) => variations.add(variation));
  } else if (doubleNinePos === 2) {
    const oldFormat = `${numberWithoutCountry.substring(0, 2)}9${numberWithoutCountry.substring(4)}`;
    getVariationsWithPrefixes(oldFormat, withPlus).forEach((variation) => variations.add(variation));
  }
  return Array.from(variations);
};

export const getBrazil9Variations = (phone: string, withPlus: boolean = false) => {
  const variations = new Set<string>();
  const numberWithoutCountry = phone.substring(2);
  // Find positions of 9s for efficient processing
  const firstNinePos = numberWithoutCountry.indexOf("9");
  const single9Variations = getSingle9BrazilVariations(phone, withPlus);
  single9Variations.forEach((variation) => {
    variations.add(variation);
  });

  if (firstNinePos === 2 && single9Variations.length === 0) {
    // Format: XX9XXXXXXXX → XX99XXXXXXX (add one 9)
    const newFormat = `${numberWithoutCountry.substring(0, 2)}99${numberWithoutCountry.substring(3)}`;
    getVariationsWithPrefixes(newFormat, withPlus).forEach((variation) => variations.add(variation));
  } else if (firstNinePos > 2 && single9Variations.length === 0 && phone.length >= 12) {
    const newFormat = `${phone.substring(2, 4)}9${phone.substring(4)}`;
    variations.add(newFormat);
    const newFormat2 = `0${phone.substring(2, 4)}9${phone.substring(4)}`;
    variations.add(newFormat2);
  }

  return Array.from(variations);
};

export const brazilPhoneVariations = (phone?: string) => {
  if (!phone) return [];

  const variations = new Set<string>();

  const addVariationsWithPrefixes = (baseNumber: string) => {
    getVariationsWithPrefixes(baseNumber, true).forEach((variation) => {
      variations.add(variation);
    });
  };

  try {
    variations.add(phone);

    // Add last 9 digits if phone is long enough
    if (phone.length > 7) {
      variations.add(phone.substring(phone.length - 9));
    }

    // Only process Brazilian numbers (starting with 55)
    if (!phone.startsWith("55")) {
      return Array.from(variations);
    }

    // Add the full number with + prefix
    variations.add(`+${phone}`);

    // Extract number without country code
    const numberWithoutCountry = phone.substring(2);
    variations.add(numberWithoutCountry);

    const brazil9Variations = getBrazil9Variations(phone, true);
    brazil9Variations.forEach((variation) => {
      variations.add(variation);
    });

    // Handle 10-digit numbers (add extra 9 after area code)
    if (numberWithoutCountry.length === 10) {
      const withExtraNine = `${numberWithoutCountry.substring(0, 2)}9${numberWithoutCountry.substring(2)}`;
      addVariationsWithPrefixes(withExtraNine);
    }
  } catch (err) {
    // ignore
  }

  return Array.from(variations);
};

/**
 * Normalizes a Brazilian mobile phone number to the canonical format
 * (55 + DDD + 9 + 8 digits).
 */
export function formatBRPhone(input: string): string {
  const digits = input.replace(/\D+/g, "");
  if (!digits.startsWith("55") || (digits.length !== 12 && digits.length !== 13)) return digits;

  const cc = digits.slice(0, 2);
  const ddd = digits.slice(2, 4);
  const subscriber = digits.slice(4);

  // Legacy mobile: 8 digits starting with [6–9]
  if (subscriber.length === 8 && /^[6-9]/.test(subscriber)) {
    return `${cc}${ddd}9${subscriber}`;
  }

  return digits;
}
